import { Fragment, useEffect, useState } from 'react';
import { useNavigate } from "react-router-dom";

/* MobX */
import { observer } from 'mobx-react-lite';

import { getAppealById, getAppealDetail, reviewLegalOwner, assignReviewer, getFile, postReviewFile } from '../../../../../services/APIClient';

/* MUI */
import { makeStyles } from '@material-ui/core/styles';
import CssBaseline from '@material-ui/core/CssBaseline';
import { Container, Paper, Typography, LinearProgress } from '@material-ui/core';

/* Langan Components */
import AppealInfo from '../../appeal-info/appeal-info.component';
import AppealValidation from '../../appeal-validation/appeal-validation.component';
import DocumentsReview from '../../documents-review/documents-review.component';
import IncorrectOwnerAppealReview from './incorrect-owner-appeal-review.component';
import ReviewButton from '../../../../form-input/form-button-review.component';

const useStyles = makeStyles((theme) => ({
  heroContent: {
    backgroundColor: theme.palette.background.paper,
    padding: theme.spacing(8, 0, 6),
  },
  paper: {
    padding: theme.spacing(2),
    marginTop: "20px",
    marginBottom: theme.spacing(5)
  },
  title: {
    marginTop: '20px',
    backgroundColor: "whitesmoke"
  },
}));

const defaultDocReview = {
  r_Deed_Submitted: false,
  r_Additional_Documents: false
};

const defaultDecisionReview = {
  r_Reviewer_Comments: "",
  r_Decision: "inprogress",
  r_Effective_Billing_Date: new Date().toISOString(),
  r_Pending_Doc_Reason: "",
  r_Denied_Reason: "",
  r_Cancelled_Reason: ""
};

const ReviewIncorrectOwnerAppeal = observer(props => {
  const classes = useStyles();
  let navigate = useNavigate();

  const [masterData, setMasterData] = useState({});
  const [appealData, setAppealData] = useState({});
  const [initStatus, setInitStatus] = useState(false);
  const [docReview, setDocReview] = useState(defaultDocReview);
  const [assessorBlock, setAssessorBlock] = useState("");
  const [file, setFile] = useState([]);
  const [decisionReview, setDecisionReview] = useState(defaultDecisionReview);
  const [reviewFileName, setReviewFileName] = useState("");
  const [reviewFile, setReviewFile] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    getAppealById(props.appealid)
      .then((response) => {
        setMasterData(response);

        //get file data
        getFile(response.masterId, response.appeal_type_id, response.appeal_type)
          .then(response => {
            if (response.status == 200) {
              setFile(response.data);
            }
          })

        getAppealDetail("AppealsLegalOwner", response.appeal_type_id)
          .then((response) => {
            setAppealData(response);
            setAssessorBlock(response.s_Assessor_Block_Lot ? response.s_Assessor_Block_Lot : "");
            if (response?.r_Decision === "rejected" || response?.r_Decision === "cancelled" || response?.r_Decision === "approved") setInitStatus(true);
            setDocReview({
              r_Deed_Submitted: response.r_Deed_Submitted === "true" ? true : false,
              r_Additional_Documents: response.r_Additional_Documents === "true" ? true : false
            })
            setDecisionReview({
              r_Reviewer_Comments: response?.r_Reviewer_Comments ? response.r_Reviewer_Comments : "",
              r_Decision: response?.r_Decision ? response.r_Decision : "inprogress",
              r_Effective_Billing_Date: response?.r_Effective_Billing_Date ? new Date(response.r_Effective_Billing_Date) : new Date(),
              r_Pending_Doc_Reason: response?.r_Pending_Doc_Reason ? response.r_Pending_Doc_Reason : "",
              r_Denied_Reason: response?.r_Denied_Reason ? response.r_Denied_Reason : "",
              r_Cancelled_Reason: response?.r_Cancelled_Reason ? response.r_Cancelled_Reason : ""
            })
          })
      })
      .catch((err) => {
        //console.log(err)
      })
  }, [props.appealid])

  const docChange = (event) => {
    const { name, checked } = event.target
    setDocReview(prev => {
      return {
        ...prev,
        [name]: checked
      }
    })
  };

  const fileChange = (event) => {
    if (event) {
      setReviewFileName(event.target.files[0].name);
      setReviewFile(event.target.files[0]);
    } else {
      setReviewFileName("");
      setReviewFile("");
    }
  };

  const submitReview = async () => {
    let today = new Date();
    today = today.toISOString();
    setSubmitting(true);
    const review = {
      ...appealData,
      r_Deed_Submitted: docReview.r_Deed_Submitted ? "true" : "false",
      r_Additional_Documents: docReview.r_Additional_Documents ? "true" : "false",
      ...decisionReview,
      r_Effective_Billing_Date: decisionReview.r_Effective_Billing_Date.toISOString(),
      r_Status_Change_Date: today, // temp set to today
      r_Review_Submitted_By: masterData.reviewer ? masterData.reviewer : props.user.email,
    };
    
    if (decisionReview.r_Decision === "pending") {
      review.r_Pending_Doc_Date = today;
    } else if (decisionReview.r_Decision === "cancelled") {
      review.r_Cancelled_Date = today;
    } else if (decisionReview.r_Decision === "rejected") {
      review.r_Denied_Date = today;
    } else if (decisionReview.r_Decision === "approved") {
      review.r_Approved_Date = today;
    }

    const reviewResult = await reviewLegalOwner(appealData.appealId, review);

    // submit review file
    if (reviewFile !== "") {
      const postFile = new FormData();
      postFile.append("master_id", masterData.masterId);
      postFile.append("appeal_type_id", masterData.appeal_type_id);
      postFile.append("appeal_type", masterData.appeal_type);
      postFile.append("AttachmentFile", reviewFile);
      postFile.append("FileName", reviewFileName);
      const fileResult = await postReviewFile(postFile);
    }

    // assign user
    const user = {
      customer_appeal_id: masterData.customer_appeal_id,
      masterId: masterData.masterId,
      appeal_type: masterData.appeal_type,
      appeal_type_id: masterData.appeal_type_id,
      ccb_account: masterData.ccb_account,
      zipcode: masterData.zipcode,
      date_of_appeal: masterData.date_of_appeal,
      nextBillDate: masterData.nextBillDate,
      reviewer: masterData.reviewer ? masterData.reviewer : props.user.email,
      applicant_name: masterData.applicant_name
    }
    const assignRsult = await assignReviewer(masterData.customer_appeal_id, user);
    setSubmitting(false);
  };

  const handleClick = async (event) => {
    event.preventDefault();
    submitReview()
      .then(result => {
        navigate("/dashboard");
      }).catch((err) => {
        //console.log(err);
        setSubmitting(false);
      });
  }

  return (
    <Fragment>
      <CssBaseline />
      <main>
        <div className={classes.heroContent} style={{ backgroundColor: "whitesmoke" }}>
          <Container maxWidth="md">
            <Typography component="h2" variant="h3" align="center" color="textPrimary" gutterBottom>
              Incorrect Owner Appeal Review
            </Typography>
          </Container>
        </div>

        <Container maxWidth="md">
          <Paper className={classes.paper}>
            <AppealInfo title="Incorrect Owner" masterData={masterData} assessorBlock={assessorBlock} type="0" />
            <AppealValidation masterData={masterData} file={file} />
            {/* <TechnicalReview /> */}
            <DocumentsReview docReview={docReview} docChange={docChange} type="0" role={props.role} initStatus={initStatus} />

            <IncorrectOwnerAppealReview
              title="Incorrect Owner Appeal Technical Review" // old title of the review section: "Incorrect Owner Appeal Information Review" 
              appealData={appealData}
              setAppealData={setAppealData}
              decisionReview={decisionReview}
              decisionReviewOnChange={setDecisionReview}
              file={file}
              fileName={reviewFileName}
              fileChange={fileChange}
              role={props.role}
              initStatus={initStatus}
              masterData={masterData}
            />
            <ReviewButton
              criteria1={(props.role.admin || props.role.business) && !initStatus}
              criteria2={decisionReview.r_Decision === "inprogress"}
              status={submitting}
              handleClick={handleClick}
            />
            {submitting && <LinearProgress />}
          </Paper>
        </Container>
      </main>
    </Fragment>
  );
});

export default ReviewIncorrectOwnerAppeal;